/* ==========================================================================
   Dideel Time — storefront JS (vanilla, deferred, no dependencies)
   - COD quick-order popup (built-in mode) + app-mode bridge
   - WhatsApp pre-filled order message
   - Sticky mobile COD bar
   - Hero slider (CSS scroll-snap)
   - Colour dots on variant pills
   - Recently viewed products
   - Tracking helpers (only fire when pixels are loaded by the theme)
   ========================================================================== */
(() => {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const BN_DIGITS = '০১২৩৪৫৬৭৮৯';
  const store = {
    get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } }
  };
  const money = (cents) => '৳' + Math.round((cents || 0) / 100).toLocaleString('en-US');

  /* ---------------- Tracking (theme pixels only) ---------------- */
  const track = (event, data) => {
    if (!window.DT_TRACK) return; // official apps handle events when theme pixels are off
    try {
      const value = data && data.value ? data.value : undefined;
      const fb = { content_ids: data && data.ids, content_type: 'product', value, currency: 'BDT', num_items: data && data.qty };
      if (window.fbq) window.fbq('track', event, fb);
      if (window.ttq) window.ttq.track(event === 'Purchase' ? 'CompletePayment' : event, fb);
      const gaMap = { ViewContent: 'view_item', AddToCart: 'add_to_cart', InitiateCheckout: 'begin_checkout', Purchase: 'purchase', Lead: 'generate_lead' };
      const ga = { currency: 'BDT', value, items: (data && data.ids || []).map((id) => ({ item_id: String(id), quantity: data.qty || 1 })) };
      if (window.gtag) window.gtag('event', gaMap[event] || event, ga);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'dt_' + (gaMap[event] || event), ecommerce: ga });
    } catch (e) { /* never break the page */ }
  };
  window.DTTrack = track;

  /* ---------------- Product data helpers ---------------- */
  const getProduct = (id) => {
    const el = $(`script[data-dt-product-json="${id}"]`);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };
  const currentVariantId = (sectionId) => {
    const form = document.getElementById(`product-form-${sectionId}`);
    const input = form && form.querySelector('input[name="id"]');
    return input ? Number(input.value) : null;
  };
  const currentQty = (sectionId) => {
    const q = document.getElementById(`Quantity-${sectionId}`);
    const n = q ? parseInt(q.value, 10) : 1;
    return n > 0 ? n : 1;
  };
  const variantOf = (p, id) => (p && (p.variants.find((v) => v.id === id) || p.variants.find((v) => v.available) || p.variants[0])) || null;

  /* ---------------- Phone ---------------- */
  const normalizePhone = (raw) => {
    let s = String(raw || '').replace(/[০-৯]/g, (d) => BN_DIGITS.indexOf(d));
    s = s.replace(/[^\d+]/g, '');
    if (s.startsWith('+880')) s = '0' + s.slice(4);
    else if (s.startsWith('880')) s = '0' + s.slice(3);
    else if (s.startsWith('+88')) s = s.slice(3);
    else if (s.length === 10 && s.startsWith('1')) s = '0' + s;
    return s;
  };
  const validPhone = (s) => /^01[3-9]\d{8}$/.test(s);

  /* ---------------- Lead capture (Shopify contact form → store email) ---------------- */
  const sendLead = (fields, kind) => {
    if (!window.DTCOD || !window.DTCOD.leads) return;
    const key = 'dt_lead_' + kind + '_' + fields.phone;
    if (sessionStorage.getItem(key)) return;
    try { sessionStorage.setItem(key, '1'); } catch (e) {}
    const body = new URLSearchParams();
    body.append('form_type', 'contact');
    body.append('utf8', '✓');
    body.append('contact[name]', fields.name || '(no name)');
    body.append('contact[email]', 'cod-lead+' + fields.phone + '@noreply.dideeltime.com');
    body.append('contact[phone]', fields.phone);
    body.append('contact[tags]', 'cod-lead,' + kind);
    body.append('contact[body]',
      `[${kind === 'full' ? 'COD ORDER FORM SUBMITTED' : 'INCOMPLETE COD LEAD'}]\n` +
      `Name: ${fields.name || ''}\nPhone: ${fields.phone}\nDistrict: ${fields.district || ''}\nArea: ${fields.area || ''}\n` +
      `Address: ${fields.address || ''}\nProduct: ${fields.product || ''}\nQty: ${fields.qty || ''}\nTotal: ${fields.total || ''}\nNote: ${fields.note || ''}\nPage: ${location.href}`);
    fetch('/contact', { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }).catch(() => {});
  };

  /* ---------------- COD modal ---------------- */
  const COD = {
    el: null, form: null, product: null, variant: null, qty: 1, lastFocus: null, dupConfirmed: false,
    init() {
      this.el = $('#dt-cod');
      if (!this.el) return;
      this.form = $('#dt-cod-form', this.el);
      this.el.addEventListener('click', (e) => { if (e.target.closest('[data-dt-close]')) this.close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close(); });
      this.el.addEventListener('keydown', (e) => this.trap(e));
      $$('[data-dt-qty]', this.el).forEach((b) => b.addEventListener('click', () => {
        this.qty = Math.min(10, Math.max(1, this.qty + Number(b.dataset.dtQty)));
        this.render();
      }));
      this.form.addEventListener('change', (e) => {
        if (e.target.name === 'district') {
          const inside = /^Dhaka$/i.test(e.target.value);
          this.form.area.value = inside ? 'inside' : 'outside';
        }
        this.render();
      });
      const phone = this.form.phone;
      phone.addEventListener('blur', () => {
        const p = normalizePhone(phone.value);
        if (validPhone(p)) {
          phone.value = p;
          const f = this.fields();
          setTimeout(() => { if (!this.submitted) sendLead(this.fields(), 'partial'); }, 45000);
          void f;
        }
      });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
    },
    open(product, variantId, qty, trigger) {
      this.product = product;
      this.variant = variantOf(product, variantId);
      if (!this.variant) return;
      this.qty = qty || 1;
      this.lastFocus = trigger || document.activeElement;
      this.dupConfirmed = false;
      this.submitted = false;
      $('[data-dt-error]', this.el).hidden = true;
      const img = $('[data-dt-img]', this.el);
      const src = this.variant.image || product.image;
      if (src) { img.src = src; img.alt = product.title; img.hidden = false; } else { img.hidden = true; }
      $('[data-dt-title]', this.el).textContent = product.title;
      const vt = this.variant.title && this.variant.title !== 'Default Title' ? this.variant.title : '';
      $('[data-dt-variant]', this.el).textContent = vt;
      this.render();
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.form.name.focus({ preventScroll: true }), 60);
      track('InitiateCheckout', { ids: [this.variant.id], qty: this.qty, value: this.variant.price * this.qty / 100 });
    },
    close() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },
    trap(e) {
      if (e.key !== 'Tab') return;
      const f = $$('button, [href], input:not([type=hidden]):not([tabindex="-1"]), select, textarea', this.el).filter((x) => !x.disabled && x.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },
    totals() {
      const c = window.DTCOD;
      const upsell = this.form.upsell && this.form.upsell.checked ? Number(this.form.upsell.dataset.price) : 0;
      const sub = this.variant.price * this.qty + upsell;
      const area = this.form.area.value;
      let ship = area === 'outside' ? c.outside : c.inside;
      if (c.freeOver > 0 && sub >= c.freeOver) ship = 0;
      return { sub, ship, total: sub + ship, upsell };
    },
    render() {
      const t = this.totals();
      $('[data-dt-unit]', this.el).textContent = money(this.variant.price);
      $('[data-dt-qty-out]', this.el).textContent = this.qty;
      $('[data-dt-subtotal]', this.el).textContent = money(t.sub);
      $('[data-dt-shipping]', this.el).textContent = t.ship === 0 ? 'ফ্রি' : money(t.ship);
      $('[data-dt-total]', this.el).textContent = money(t.total);
      const adv = $('[data-dt-advance]', this.el);
      if (adv) adv.hidden = this.form.area.value !== 'outside';
    },
    fields() {
      const f = this.form;
      return {
        name: f.name.value.trim(),
        phone: normalizePhone(f.phone.value),
        address: f.address.value.trim(),
        district: f.district ? f.district.value : '',
        area: f.area.value === 'outside' ? 'Outside Dhaka' : 'Inside Dhaka',
        note: f.note.value.trim(),
        product: this.product ? `${this.product.title}${this.variant && this.variant.title !== 'Default Title' ? ' / ' + this.variant.title : ''}` : '',
        qty: this.qty,
        total: money(this.totals().total)
      };
    },
    validate() {
      const f = this.form;
      const d = this.fields();
      const set = (input, bad) => { input.closest('.dt-field').classList.toggle('has-error', bad); input.setAttribute('aria-invalid', bad ? 'true' : 'false'); return bad; };
      const errs = [
        set(f.name, d.name.length < 2),
        set(f.phone, !validPhone(d.phone)),
        f.district ? set(f.district, !d.district) : false,
        set(f.address, d.address.length < 10)
      ];
      const firstBad = $('.has-error input, .has-error textarea, .has-error select', f);
      if (firstBad) firstBad.focus();
      return !errs.some(Boolean);
    },
    submit() {
      const err = $('[data-dt-error]', this.el);
      err.hidden = true;
      if (this.form.website.value) return; // honeypot → bot
      if (!this.validate()) return;
      const d = this.fields();
      this.form.phone.value = d.phone;

      // Duplicate protection (same phone within X hours on this device)
      const hrs = window.DTCOD.dupHours;
      const log = store.get('dt_cod_log', {});
      if (hrs > 0 && log[d.phone] && Date.now() - log[d.phone] < hrs * 3600e3 && !this.dupConfirmed) {
        err.textContent = 'এই নম্বর থেকে কিছুক্ষণ আগে একটি অর্ডার এসেছে। নতুন অর্ডার করতে চাইলে আবার "অর্ডার কনফার্ম করুন" চাপুন।';
        err.hidden = false;
        this.dupConfirmed = true;
        return;
      }

      const btn = $('[data-dt-submit]', this.el);
      btn.disabled = true;
      btn.querySelector('.dt-cod-btn__main').insertAdjacentHTML('afterbegin', '<span class="dt-spinner" aria-hidden="true"></span>');
      this.submitted = true;
      log[d.phone] = Date.now();
      store.set('dt_cod_log', log);
      sendLead(d, 'full');

      const t = this.totals();
      track('Lead', { ids: [this.variant.id], qty: this.qty, value: t.total / 100 });

      // Build Shopify checkout permalink (one step: cart → checkout with details pre-filled)
      const items = [`${this.variant.id}:${this.qty}`];
      if (t.upsell) items.push(`${this.form.upsell.value}:1`);
      const parts = d.name.split(/\s+/);
      const last = parts.pop();
      const first = parts.join(' ');
      const q = new URLSearchParams();
      q.set('checkout[shipping_address][first_name]', first);
      q.set('checkout[shipping_address][last_name]', last);
      q.set('checkout[shipping_address][address1]', d.address);
      q.set('checkout[shipping_address][city]', d.district || (d.area === 'Inside Dhaka' ? 'Dhaka' : ''));
      q.set('checkout[shipping_address][country]', 'BD');
      q.set('checkout[shipping_address][phone]', d.phone);
      q.set('attributes[Phone]', d.phone);
      q.set('attributes[Delivery area]', d.area);
      q.set('attributes[Order source]', 'COD form');
      if (d.note) q.set('note', d.note);
      q.set('ref', 'cod-form');
      window.location.href = `/cart/${items.join(',')}?${q.toString()}`;
      setTimeout(() => { btn.disabled = false; const s = btn.querySelector('.dt-spinner'); if (s) s.remove(); }, 8000);
    }
  };

  /* Open COD from any trigger */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-dt-cod-open]');
    if (!trigger) return;
    e.preventDefault();
    const mode = window.DT_COD_MODE || 'builtin';
    const sectionId = trigger.dataset.section;
    if (mode === 'app') {
      const sel = window.DT_COD_APP_SELECTOR;
      const appBtn = sel && document.querySelector(sel);
      if (appBtn) { appBtn.click(); return; }
      // fall back: go to product page where the app form lives
      if (trigger.dataset.url) window.location.href = trigger.dataset.url;
      return;
    }
    const product = getProduct(trigger.dataset.productId);
    if (!product) { if (trigger.dataset.url) window.location.href = trigger.dataset.url; return; }
    const vid = sectionId ? currentVariantId(sectionId) : Number(trigger.dataset.variantId) || null;
    const qty = sectionId ? currentQty(sectionId) : 1;
    COD.open(product, vid, qty, trigger);
  });

  /* ---------------- WhatsApp pre-filled message ---------------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-dt-wa]');
    if (!a) return;
    const p = getProduct(a.dataset.productId);
    const v = p ? variantOf(p, currentVariantId(a.dataset.section)) : null;
    const lines = ['আসসালামু আলাইকুম, আমি এই ঘড়িটি অর্ডার করতে চাই:', a.dataset.title];
    if (v && v.title !== 'Default Title') lines.push('Variant: ' + v.title);
    if (v) lines.push('Price: ' + money(v.price));
    if (a.dataset.section) lines.push('Qty: ' + currentQty(a.dataset.section));
    lines.push(a.dataset.url);
    const base = a.getAttribute('href').split('?')[0];
    a.setAttribute('href', `${base}?text=${encodeURIComponent(lines.join('\n'))}`);
  });

  /* ---------------- Sticky mobile COD bar ---------------- */
  const initSticky = () => {
    const bar = $('[data-dt-sticky]');
    const main = $('.product__info-container [data-dt-cod-open]');
    if (!bar || !main) return;
    document.body.classList.add('dt-has-sticky-ready');
    const priceEl = $('[data-dt-sticky-price]', bar);
    const sectionId = main.dataset.section;
    const p = getProduct(main.dataset.productId);
    const update = () => {
      const v = variantOf(p, currentVariantId(sectionId));
      if (v && priceEl) priceEl.textContent = money(v.price);
      const b = $('[data-dt-cod-open]', bar);
      if (b && v) b.disabled = !v.available;
    };
    update();
    document.addEventListener('change', (e) => { if (e.target.closest('variant-selects, .product-form__input')) setTimeout(update, 400); });
    const io = new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', past);
      bar.setAttribute('aria-hidden', past ? 'false' : 'true');
      document.body.classList.toggle('dt-has-sticky', past);
    });
    io.observe(main);
  };

  /* Keep COD button disabled state in sync with variant availability */
  const initVariantSync = () => {
    const btn = $('.product__info-container [data-dt-cod-open]');
    if (!btn) return;
    const p = getProduct(btn.dataset.productId);
    const sync = () => {
      const v = variantOf(p, currentVariantId(btn.dataset.section));
      if (v) btn.disabled = !v.available;
      const sku = $('[data-dt-sku]');
      if (sku && v) sku.textContent = v.sku || '—';
    };
    document.addEventListener('change', (e) => { if (e.target.closest('variant-selects, .product-form__input')) setTimeout(sync, 400); });
  };

  /* ---------------- Colour dots for colour options ---------------- */
  const COLORS = {
    black: '#0a0a0a', white: '#ffffff', silver: '#c0c0c0', gold: '#d4a843', 'rose gold': '#d9a38f', rosegold: '#d9a38f',
    blue: '#1f3a8a', navy: '#1b2a4a', green: '#2f5d3a', brown: '#6b4226', grey: '#8a8a8a', gray: '#8a8a8a',
    champagne: '#e8d9b5', cream: '#f2ead8', red: '#8b1e1e', tan: '#b58b5a', 'two tone': 'linear-gradient(90deg,#c0c0c0 50%,#d4a843 50%)'
  };
  const initSwatches = () => {
    $$('variant-selects fieldset').forEach((fs) => {
      const legend = (fs.querySelector('legend') || {}).textContent || '';
      if (!/colou?r|রং|dial|strap/i.test(legend)) return;
      $$('input[type="radio"]', fs).forEach((inp) => {
        const label = fs.querySelector(`label[for="${inp.id}"]`);
        if (!label || label.querySelector('.swatch')) return;
        const key = inp.value.toLowerCase().replace(/\s*(dial|strap|leather|chain|mesh)\s*/g, ' ').trim();
        const col = COLORS[key] || COLORS[key.split(' ')[0]];
        if (col) { label.dataset.dtColor = ''; label.style.setProperty('--dt-swatch', col); }
      });
    });
  };

  /* ---------------- Hero slider ---------------- */
  const initHero = () => {
    $$('[data-dt-hero]').forEach((hero) => {
      const track = $('.dt-hero__track', hero);
      const slides = $$('.dt-hero__slide', hero);
      const dots = $$('.dt-hero__dots button', hero);
      if (slides.length < 2) return;
      let i = 0, timer = null;
      const go = (n) => { i = (n + slides.length) % slides.length; track.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' }); };
      const mark = () => dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
      dots.forEach((d, k) => d.addEventListener('click', () => { go(k); stop(); }));
      track.addEventListener('scroll', () => {
        const n = Math.round(track.scrollLeft / track.clientWidth);
        if (n !== i) { i = n; mark(); }
      }, { passive: true });
      const speed = Number(hero.dataset.speed || 0) * 1000;
      const start = () => { if (speed && !timer && !matchMedia('(prefers-reduced-motion: reduce)').matches) timer = setInterval(() => go(i + 1), speed); };
      const stop = () => { clearInterval(timer); timer = null; };
      hero.addEventListener('pointerdown', stop, { passive: true });
      hero.addEventListener('focusin', stop);
      new IntersectionObserver(([en]) => (en.isIntersecting ? start() : stop())).observe(hero);
      mark();
    });
  };

  /* ---------------- Recently viewed ---------------- */
  const initRecent = () => {
    const current = window.DT_PRODUCT_HANDLE;
    let list = store.get('dt_recent', []);
    if (current) {
      list = [current, ...list.filter((h) => h !== current)].slice(0, 10);
      store.set('dt_recent', list);
    }
    $$('[data-dt-recent]').forEach(async (wrap) => {
      const handles = list.filter((h) => h !== current).slice(0, Number(wrap.dataset.limit || 4));
      if (!handles.length) return;
      const grid = $('[data-dt-recent-grid]', wrap);
      const items = await Promise.all(handles.map((h) => fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      const html = items.filter(Boolean).map((p) => {
        const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=400` : '';
        return `<li class="grid__item"><a class="dt-recent-card" href="${p.url}">
          <span class="dt-recent-card__img">${img ? `<img src="${img}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy" width="400" height="400">` : ''}</span>
          <span class="dt-recent-card__title">${p.title}</span><span class="dt-recent-card__price dt-num">${money(p.price)}</span></a></li>`;
      }).join('');
      if (html) { grid.innerHTML = html; wrap.hidden = false; }
    });
  };

  /* ---------------- Add-to-cart tracking via Dawn pubsub ---------------- */
  const initCartTracking = () => {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (ev) => {
        if (ev && ev.source === 'product-form' && ev.productVariantId) track('AddToCart', { ids: [ev.productVariantId], qty: 1 });
      });
    }
    document.addEventListener('click', (e) => {
      if (e.target.closest('[name="checkout"], #CartDrawer-Checkout, #checkout')) track('InitiateCheckout', {});
    });
  };

  /* ---------------- Order tracking page ---------------- */
  const initTracking = () => {
    const root = $('[data-dt-track]');
    if (!root) return;
    const tabs = $$('[role="tab"]', root);
    tabs.forEach((t) => t.addEventListener('click', () => {
      tabs.forEach((x) => { x.setAttribute('aria-selected', x === t ? 'true' : 'false'); $('#' + x.getAttribute('aria-controls')).hidden = x !== t; });
    }));
    const orderForm = $('[data-dt-track-order]', root);
    if (orderForm) orderForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const order = orderForm.order.value.replace(/[^\dA-Za-z#-]/g, '');
      const phone = normalizePhone(orderForm.phone.value);
      const err = $('[data-dt-track-error]', orderForm);
      if (!order || !validPhone(phone)) { err.hidden = false; return; }
      err.hidden = true;
      const msg = `আসসালামু আলাইকুম, আমার অর্ডারের স্ট্যাটাস জানতে চাই।\nOrder: ${order}\nPhone: ${phone}`;
      window.open(`https://wa.me/${root.dataset.wa}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
    });
    const courierForm = $('[data-dt-track-courier]', root);
    if (courierForm) courierForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const code = courierForm.code.value.trim();
      const base = courierForm.courier.value;
      if (!code || !base) return;
      window.open(base + encodeURIComponent(code), '_blank', 'noopener');
    });
  };


  /* ---------------- Collection: "Load more" instead of page numbers ---------------- */
  const initLoadMore = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const setup = () => {
      const grid = document.getElementById('product-grid');
      const pag = container.querySelector('.pagination-wrapper');
      if (!grid || !pag || container.querySelector('[data-dt-more]')) return;
      const nextLink = () => {
        const links = Array.from(pag.querySelectorAll('a[href*="page="]'));
        const cur = Number(new URL(window.location.href).searchParams.get('page') || grid.dataset.dtPage || 1);
        return links.find((a) => Number(new URL(a.href, location.origin).searchParams.get('page')) === cur + 1);
      };
      let next = nextLink();
      if (!next) return;
      pag.style.display = 'none';
      const wrap = document.createElement('div');
      wrap.style.cssText = 'text-align:center;margin-top:3.2rem';
      wrap.innerHTML = '<button type="button" class="dt-btn dt-btn--outline" data-dt-more>আরও দেখুন</button>';
      pag.after(wrap);
      const btn = wrap.querySelector('button');
      let url = next.href;
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.insertAdjacentHTML('afterbegin', '<span class="dt-spinner" aria-hidden="true"></span>');
        try {
          const html = await fetch(url).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = doc.querySelectorAll('#product-grid > li');
          items.forEach((li) => grid.appendChild(li));
          const np = doc.querySelector('.pagination-wrapper');
          const pageNum = Number(new URL(url, location.origin).searchParams.get('page'));
          grid.dataset.dtPage = pageNum;
          const n = np && Array.from(np.querySelectorAll('a[href*="page="]')).find((a) => Number(new URL(a.href, location.origin).searchParams.get('page')) === pageNum + 1);
          if (n) { url = n.href; btn.disabled = false; const sp = btn.querySelector('.dt-spinner'); if (sp) sp.remove(); }
          else wrap.remove();
        } catch (e) { window.location.href = url; }
      });
    };
    setup();
    new MutationObserver(() => setup()).observe(container, { childList: true });
  };

  /* ---------------- Deferred third-party loading ---------------- */
  const onFirstInteraction = (fn) => {
    let done = false;
    const run = () => { if (done) return; done = true; ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((ev) => window.removeEventListener(ev, run)); fn(); };
    ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach((ev) => window.addEventListener(ev, run, { once: true, passive: true }));
    setTimeout(run, 6000);
  };
  window.DTOnInteraction = onFirstInteraction;

  const boot = () => {
    COD.init();
    initSticky();
    initVariantSync();
    initSwatches();
    initHero();
    initRecent();
    initCartTracking();
    initTracking();
    initLoadMore();
    const pid = window.DT_PRODUCT_VIEW;
    if (pid) onFirstInteraction(() => setTimeout(() => track('ViewContent', pid), 300));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', () => { initHero(); initSwatches(); });
})();
